import { Button, Form, Row, Spin } from "antd";
import { useDateRange } from "context/DateRangeContext";
import { useClient } from "hooks/useClient";
import { useCallback, useContext, useEffect, useState } from "react";
import DistributionPointSelected from "context/MyDistributionPoint";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import { FormItem } from "common/components/form/FormItem";
import {
  DateFilterType,
  DateRangePicker,
} from "common/components/widgets/datePicker/DateRangePicker";
import { usePaginationTable } from "hooks/commons/usePaginationTable";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import {
  downloadDictumFile,
  getDictums,
  ResponseGetDictumsDetails,
} from "api/generated";
import {
  handleAxiosError,
  handleJsonDownload,
  handlePdfDownload,
} from "common/helpers/generalUtils";
import { useIsMobile } from "hooks/useIsMobile";
import DictumsTable from "./components/DictumsTable";
import { querySerializer } from "common/helpers/queryUtils";

function DictumsScreen(props: RouteComponentProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { rangeTimeValue, setRangeTimeValue, getFormattedDates } =
    useDateRange();
  const { startDate, endDate } = getFormattedDates();
  const { client } = useClient();
  const { distributionPoint } = useContext(DistributionPointSelected);
  const { isMobile } = useIsMobile();

  const pagination = usePaginationTable<ResponseGetDictumsDetails>(props, {
    initialSort: ["creationDate,DESC"],
  });

  const reloadTable = useCallback(() => {
    if (!distributionPoint) {
      return;
    }

    setIsLoading(true);
    getDictums({
      client,
      querySerializer: querySerializer,
      query: {
        request: {
          dictumStartDate: startDate,
          dictumEndDate: endDate,
          dictumDisplayPage: pagination.currentPage,
          dictumDisplayAmount: pagination.pageSize,
          distributionPointId: distributionPoint.id,
        },
        pageable: {
          page: pagination.currentPage,
          size: pagination.pageSize,
          sort: pagination.sort,
        },
      },
    })
      .then(({ data }) => {
        pagination.setTotalItems(data?.totalElements ?? 0);
        pagination.setItems(data?.content ?? []);
      })
      .catch((err) =>
        handleAxiosError(err, "No fue posible obtener dictamenes"),
      )
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    client,
    startDate,
    endDate,
    distributionPoint,
    pagination.currentPage,
    pagination.pageSize,
    pagination.sort,
  ]);

  // useEffect principal
  useEffect(() => {
    reloadTable();
  }, [reloadTable]);

  function handleDownload(dictumId: string, fileType: "pdf" | "json") {
    downloadDictumFile({
      client,
      responseType: fileType === "pdf" ? "arraybuffer" : `json`,
      path: {
        dictumId,
        fileType,
      },
    })
      .then((data) => {
        if (fileType === "pdf") {
          handlePdfDownload(data);
        } else {
          handleJsonDownload(data);
        }
      })
      .catch((err) =>
        handleAxiosError(err, "No fue posible descargar el dictamen"),
      );
  }

  return (
    <Spin spinning={isLoading} size="large" tip="Cargando...">
      <Form layout="vertical">
        <Row gutter={16} align="bottom">
          <ResponsiveCol>
            <FormItem label="Seleccionar periodo por fecha de dictamen">
              <DateRangePicker
                config={{
                  allowedTypes: [
                    DateFilterType.DAY,
                    DateFilterType.MONTH,
                    DateFilterType.RANGE,
                  ],
                  defaultType: DateFilterType.MONTH,
                  showQuickFilters: true,
                }}
                rangeTimeValue={rangeTimeValue}
                setRangeTimeValue={(value) => {
                  pagination.resetPagination();
                  setRangeTimeValue(value);
                }}
              />
            </FormItem>
          </ResponsiveCol>
          {!isMobile && (
            <ResponsiveCol>
              <></>
            </ResponsiveCol>
          )}

          <ResponsiveCol>
            <FormItem>
              <Link
                key="createDictum"
                to={{
                  pathname: `/dictum/new`,
                  state: {
                    from: location.pathname + location.search,
                  },
                }}
              >
                <Button style={{ width: "100%" }} type="primary">
                  Agregar Dictamen
                </Button>
              </Link>
            </FormItem>
          </ResponsiveCol>
        </Row>
      </Form>
      <DictumsTable
        pagination={pagination}
        isLoading={isLoading}
        handleDownload={handleDownload}
        handleEdit={(dictumId) => {
          props.history.push(`dictum/${dictumId}`);
        }}
      />
    </Spin>
  );
}

export default withRouter(DictumsScreen);
